// src/Loan/LoanCard.jsx
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  fetchLoanEmis,
  clearLoanEmis,
} from "../redux/slices/loan/loanEmiSlice";
import { fetchAllLoans } from "../redux/slices/loan/loanReportSlice";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";

import "../assets/print/PrintLoanStatement.css";

/* ================== Utils ================== */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return isNaN(date) ? dateStr : date.toLocaleDateString("en-GB");
};

const formatAmount = (amount) =>
  !amount ? "Rs 0" : `Rs ${Number(amount).toLocaleString("en-IN")}`;

/* ================== Component ================== */
const PrintLoanStatement = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();
  const cardRef = useRef(null);

  const { emis, loading: emiLoading, error: emiError } = useSelector(
    (state) => state.loanEmi
  );
  const { loans, loading: loanLoading, error: loanError } = useSelector(
    (state) => state.loanReport
  );
  const { profile } = useSelector((state) => state.userProfile);

  const [company, setCompany] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);

  const branchId = Number(localStorage.getItem("userId")) || 0;

  /* ================== Fetch Profile ================== */
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ================== Fetch Loan & EMI ================== */
  useEffect(() => {
    if (!loanId) return;

    dispatch(fetchLoanEmis(loanId));
    if (loans.length === 0) dispatch(fetchAllLoans());

    return () => dispatch(clearLoanEmis());
  }, [dispatch, loanId, loans.length]);

  /* ================== Fetch Company ================== */
  useEffect(() => {
    if (!profile?.superAdminId) return;

    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://narrowitsolution.in/api/get-company?superAdminId=${profile.superAdminId}`
        );
        const data = await res.json();

        if (res.ok && data) {
          setCompany({
            ...data,
            logoUrl: data.logo
              ? `https://narrowitsolution.in/${data.logo.replace(/^images\//, "")}`
              : null,
          });
        }
      } catch (err) {
        console.error("Company fetch error:", err);
      }
    };

    fetchCompany();
  }, [profile?.superAdminId]);

  const loan = loans.find((l) => String(l.id) === String(loanId));

  /* ================== Fetch Customer Details & Photo ================== */
  useEffect(() => {
    if (!loan?.customerId || !profile?.superAdminId) return;

    const fetchCustomerDetails = async () => {
      try {
        const res = await fetch(
          `https://narrowitsolution.in/api/customers-get-userId?userId=${loan.customerId}&branchId=${branchId}`
        );
        const data = await res.json();
        setCustomerDetails(data?.data || null);
      } catch (err) {
        console.error("Customer fetch error:", err);
      }
    };

    const fetchCustomerPhoto = async () => {
      try {
        const res = await fetch(
          `https://narrowitsolution.in/api/list-kyc?userId=${loan.customerId}&superAdminId=${profile.superAdminId}&createdById=${branchId}`
        );
        const data = await res.json();

        const photoDoc = data?.documents?.find(
          (doc) => doc.docName?.toLowerCase() === "photo"
        );

        if (photoDoc?.docImage) {
          setPhotoUrl(`https://narrowitsolution.in/uploads/${photoDoc.docImage}`);
        }
      } catch (err) {
        console.error("Photo fetch error:", err);
      }
    };

    fetchCustomerDetails();
    fetchCustomerPhoto();
  }, [loan?.customerId, profile?.superAdminId, branchId]);

  /* ================== PDF Generator ================== */
  const generatePDF = async () => {
    if (!cardRef.current) return;

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const canvas = await html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
    });

    const imgWidth = pageWidth - 20;
    const pageImgHeight = pageHeight - 20;
    const totalHeight = (canvas.height * imgWidth) / canvas.width;

    let renderedHeight = 0;

    while (renderedHeight < totalHeight) {
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = (pageImgHeight * canvas.width) / imgWidth;

      const ctx = pageCanvas.getContext("2d");
      ctx.drawImage(
        canvas,
        0,
        (renderedHeight * canvas.width) / imgWidth,
        canvas.width,
        pageCanvas.height,
        0,
        0,
        canvas.width,
        pageCanvas.height
      );

      if (renderedHeight !== 0) pdf.addPage();

      pdf.addImage(
        pageCanvas.toDataURL("image/png"),
        "PNG",
        10,
        10,
        imgWidth,
        (pageCanvas.height * imgWidth) / canvas.width
      );

      renderedHeight += pageImgHeight;
    }

    pdf.save(`LoanCard_${loan?.loanAccountNumber || loan?.loanNumber}.pdf`);
  };

  /* ================== UI States ================== */
  if (loanLoading || emiLoading)
    return <div className="print-loan-statement-container">Loading...</div>;

  if (loanError || emiError)
    return (
      <div className="print-loan-statement-container">
        {loanError || emiError}
      </div>
    );

  if (!loan)
    return (
      <div className="print-loan-statement-container">
        No loan found for Loan ID {loanId}
      </div>
    );

  /* ================== JSX ================== */
  return (
    <div>
      <div className="print-loan-statement-container" ref={cardRef}>
        <header className="print-loan-statement-header">
          <div className="print-loan-statement-logo-box">
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="Logo"
                className="print-loan-statement-logo"
              />
            )}
          </div>

          <div className="print-loan-statement-details-box">
            <h2>{company?.name || "Company Name"}</h2>
            <p>
              <strong>CIN:</strong> {company?.cin || "-"}
              <br />
              <strong>Regt. Office:</strong> {company?.address || "-"}
              <br />
              <strong>Website:</strong> {company?.domain || "-"}
              <br />
              <strong>Contact:</strong> {company?.contact || "-"}
              <br />
              <strong>Email:</strong> {company?.email || "-"}
            </p>
          </div>
        </header>

        <div className="print-loan-statement-title-section">
          <h3 className="print-loan-statement-title">For Closer Loan Statement</h3>
        </div>

        <table className="print-loan-statement-detail-table">
          <tbody>
            <tr>
              <td>
                <strong>Customer Name:</strong> {loan.userName || "-"}
              </td>
              <td>
                <strong>Account Number:</strong>{" "}
                {loan.loanAccountNumber || loan.loanNumber}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Branch:</strong> {profile?.branchName || "-"}
              </td>
              <td>
                <strong>Date:</strong>{" "}
                {new Date().toLocaleDateString("en-GB")}
              </td>
            </tr>
          </tbody>
        </table>

        <section className="print-loan-statement-table-section">
          <table className="print-loan-statement-table">
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>EMI Amount</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Emi Due Date</th>
                <th>Payment Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {emis.map((emi, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{formatAmount(emi.emiAmount)}</td>
                  <td>{formatAmount(emi.principalComponent)}</td>
                  <td>{formatAmount(emi.interestComponent)}</td>
                  <td>{formatDate(emi.dueDate)}</td>
                  <td>{formatDate(emi.paymentDate)}</td>
                  <td>{emi.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          onClick={generatePDF}
          className="print-loan-statement-download-btn"
        >
          📄 Download PDF
        </button>
      </div>
    </div>
  );
};

export default PrintLoanStatement;
